import { cn } from '@/lib/utils';
import type { BlockColor, RichText, RockBlock, RockDocRef } from '@rock/types';
import { Check, ChevronRight } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import { getBackgroundClass, getBlockColors } from './rockColor';

const headingSizes: Record<1 | 2 | 3, string> = {
    1: 'text-3xl font-semibold',
    2: 'text-2xl font-semibold',
    3: 'text-xl font-semibold',
};

const headingMargins: Record<1 | 2 | 3, string> = {
    1: 'mt-10 first:mt-0',
    2: 'mt-8 first:mt-0',
    3: 'mt-6 first:mt-0',
};

const listMarkerForDepth = (index: number, depth: number) => {
    const safeIndex = index > 0 ? index : 1;
    const normalizedDepth = depth >= 0 ? depth : 0;
    const styleIndex = ((normalizedDepth % 3) + 3) % 3;

    if (styleIndex === 0) {
        return safeIndex.toString();
    }

    if (styleIndex === 1) {
        return numberToAlpha(safeIndex);
    }

    return numberToRoman(safeIndex);
};

const RockDocRenderer: FC<{ blocks: RockBlock[] }> = ({ blocks }) => {
    return (
        <div className="space-y-4">
            <BlocksView blocks={blocks} />
        </div>
    );
};

export default RockDocRenderer;

const BlocksView: FC<{ blocks?: RockBlock[]; listDepth?: number }> = ({ blocks, listDepth = 0 }) => {
    let numberCounter = 0;
    let previousWasNumbered = false;

    return (
        <div className="space-y-4">
            {blocks?.map((block, index) => {
                if (block.type === 'list_item' && block.style === 'number') {
                    numberCounter = previousWasNumbered ? numberCounter + 1 : 1;
                    previousWasNumbered = true;
                } else {
                    numberCounter = 0;
                    previousWasNumbered = false;
                }

                switch (block.type) {
                    case 'heading': {
                        return <HeadingBlock key={index} block={block} listDepth={listDepth} />;
                    }
                    case 'paragraph': {
                        const colors = getBlockColors(block.color);
                        return (
                            <div key={index} className={cn('space-y-2', backgroundBox(block.color))}>
                                <p className={cn('leading-7', colors.text)}>
                                    <InlineText text={block.text} />
                                </p>
                                {block.children?.length ? (
                                    <div className="pl-6">
                                        <BlocksView blocks={block.children} listDepth={listDepth} />
                                    </div>
                                ) : null}
                            </div>
                        );
                    }
                    case 'list_item': {
                        const colors = getBlockColors(block.color);
                        const marker =
                            block.style === 'number' ? `${listMarkerForDepth(numberCounter, listDepth)}.` : '•';
                        return (
                            <div key={index} className={cn('space-y-2', backgroundBox(block.color))}>
                                <div className="flex items-start gap-3 pl-1">
                                    <span className={cn('mt-1 text-sm font-semibold', colors.text)}>{marker}</span>
                                    <div className={cn('space-y-2 text-base leading-7', colors.text)}>
                                        <InlineText text={block.text} />
                                        {block.children?.length ? (
                                            <div className="pl-6">
                                                <BlocksView blocks={block.children} listDepth={listDepth + 1} />
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    case 'check': {
                        const colors = getBlockColors(block.color);
                        return (
                            <div key={index} className={cn('space-y-2', backgroundBox(block.color))}>
                                <div className="flex items-start gap-3">
                                    <div
                                        className={cn(
                                            'mt-1 flex h-5 w-5 items-center justify-center rounded-sm border',
                                            block.checked ? 'border-green-500 bg-green-500' : 'border-zinc-300',
                                        )}
                                    >
                                        {block.checked ? <Check className="h-3 w-3 text-white" /> : null}
                                    </div>
                                    <div className={cn('space-y-2 text-base leading-7', colors.text)}>
                                        <InlineText text={block.text} />
                                        {block.children?.length ? (
                                            <div className="pl-6">
                                                <BlocksView blocks={block.children} listDepth={listDepth} />
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    case 'callout': {
                        const colors = getBlockColors(block.color);
                        return (
                            <div
                                key={index}
                                className={cn(
                                    'flex gap-3 rounded-md border px-4 py-3 text-sm leading-6',
                                    colors.bg,
                                    colors.border,
                                )}
                            >
                                {block.icon ? <span className="text-xl">{block.icon}</span> : null}
                                <div className={cn('space-y-2', colors.text)}>
                                    <InlineText text={block.text} />
                                    {block.children?.length ? (
                                        <div className="pl-6">
                                            <BlocksView blocks={block.children} listDepth={listDepth} />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    }
                    case 'toggle': {
                        return <ToggleBlock key={index} block={block} listDepth={listDepth} />;
                    }
                    case 'image': {
                        return (
                            <figure key={index} className="space-y-2">
                                <img
                                    src={block.url}
                                    alt={block.alt ?? ''}
                                    className="max-h-96 w-full rounded-lg object-contain"
                                />
                                {block.caption?.length ? (
                                    <figcaption className="text-sm text-muted-foreground">
                                        <InlineText text={block.caption} />
                                    </figcaption>
                                ) : null}
                            </figure>
                        );
                    }
                    case 'divider': {
                        return <div key={index} className="h-px bg-zinc-200" />;
                    }
                    case 'quote': {
                        const color = block.color ?? 'default';
                        return (
                            <blockquote
                                key={index}
                                className={cn(
                                    'space-y-2 border-l-4 border-zinc-300 pl-4 text-base italic',
                                    color === 'default' ? 'text-zinc-700' : getBlockColors(color).text,
                                    backgroundBox(color),
                                )}
                            >
                                <InlineText text={block.text} />
                                {block.children?.length ? (
                                    <div className="pl-4">
                                        <BlocksView blocks={block.children} listDepth={listDepth} />
                                    </div>
                                ) : null}
                            </blockquote>
                        );
                    }
                    case 'code': {
                        return (
                            <figure key={index} className="space-y-2">
                                <pre className="overflow-x-auto rounded-md bg-zinc-100 p-3 font-mono text-sm">
                                    {block.text.map((t) => t.plainText).join('')}
                                </pre>
                                {block.caption?.length ? (
                                    <figcaption className="text-sm text-muted-foreground">
                                        <InlineText text={block.caption} />
                                    </figcaption>
                                ) : null}
                            </figure>
                        );
                    }
                    default:
                        return null;
                }
            })}
        </div>
    );
};

const HeadingBlock: FC<{ block: Extract<RockBlock, { type: 'heading' }>; listDepth: number }> = ({
    block,
    listDepth,
}) => {
    const colors = getBlockColors(block.color);
    const marginClass = headingMargins[block.level];
    const headingContent: ReactNode = (
        <div className={cn(headingSizes[block.level], colors.emphasisText ?? colors.text)}>
            <InlineText text={block.text} />
        </div>
    );

    if (block.toggle) {
        return (
            <Disclosure
                className={cn('space-y-2', marginClass, colors.border, colors.bg)}
                summary={headingContent}
                summaryClassName="px-4 py-3"
                chevronClassName={colors.text}
            >
                {block.children?.length ? (
                    <div className="pl-4">
                        <BlocksView blocks={block.children} listDepth={listDepth} />
                    </div>
                ) : null}
            </Disclosure>
        );
    }

    return (
        <div className={cn('space-y-2', marginClass, backgroundBox(block.color))}>
            {headingContent}
            {block.children?.length ? (
                <div className="pl-4">
                    <BlocksView blocks={block.children} listDepth={listDepth} />
                </div>
            ) : null}
        </div>
    );
};

type ToggleBlockType = Extract<RockBlock, { type: 'toggle' }>;

const ToggleBlock: FC<{ block: ToggleBlockType; listDepth: number }> = ({ block, listDepth }) => {
    const colors = getBlockColors(block.color);

    return (
        <Disclosure
            className={cn(colors.border, colors.bg)}
            summary={<InlineText text={block.summary} />}
            summaryClassName={cn('px-3 py-2 text-base font-medium', colors.text)}
            chevronClassName={colors.text}
            contentClassName="pt-2"
        >
            {block.children?.length ? (
                <div className="pl-4">
                    <BlocksView blocks={block.children} listDepth={listDepth} />
                </div>
            ) : null}
        </Disclosure>
    );
};

type DisclosureProps = {
    summary: ReactNode;
    children?: ReactNode;
    className?: string;
    summaryClassName?: string;
    chevronClassName?: string;
    contentClassName?: string;
};

const Disclosure: FC<DisclosureProps> = ({
    summary,
    children,
    className,
    summaryClassName,
    chevronClassName,
    contentClassName,
}) => {
    const [open, setOpen] = useState(false);

    return (
        <div className={cn('rounded-md border border-zinc-200 bg-white', className)}>
            <button
                type="button"
                className={cn('flex w-full items-center gap-3 px-3 py-2 text-left', summaryClassName)}
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
            >
                <ChevronRight
                    className={cn(
                        'h-4 w-4 flex-shrink-0 text-zinc-500 transition-transform duration-200',
                        chevronClassName,
                        open ? 'rotate-90' : 'rotate-0',
                    )}
                />
                <div className="flex-1">{summary}</div>
            </button>
            {open ? <div className={cn('px-3 pb-3 pt-2', contentClassName)}>{children}</div> : null}
        </div>
    );
};

const InlineText: FC<{ text: RichText[] }> = ({ text }) => {
    return (
        <>
            {text.map((segment, index) => {
                const isBold = segment.marks?.includes('bold');
                const isItalic = segment.marks?.includes('italic');
                const isUnderline = segment.marks?.includes('underline');
                const isStrike = segment.marks?.includes('strikethrough');
                const isCode = segment.marks?.includes('code');
                const isLink = !!segment.href || !!segment.ref;

                const className = cn(segment.color && inlineColorClass(segment.color), {
                    'font-semibold': isBold,
                    italic: isItalic,
                    underline: isUnderline && !isLink,
                    'line-through': isStrike,
                });

                const content = isCode ? (
                    <code className="rounded-md bg-zinc-100 px-1 py-0.5 font-mono text-xs">{segment.plainText}</code>
                ) : (
                    segment.plainText
                );

                if (segment.ref) {
                    return <RefLink key={index} docRef={segment.ref} className={className} content={content} />;
                }

                if (isLink && segment.href) {
                    return (
                        <a
                            key={index}
                            href={segment.href}
                            target="_blank"
                            rel="noreferrer"
                            className={cn(className, 'text-blue-600 underline')}
                        >
                            {content}
                        </a>
                    );
                }

                return (
                    <span key={index} className={className}>
                        {content}
                    </span>
                );
            })}
        </>
    );
};

/**
 * A link to another catalog entry, opened in the admin. A task's page sits under its consideration,
 * which the ref doesn't name, so a task link only says where it goes.
 */
const RefLink: FC<{ docRef: RockDocRef; className: string; content: ReactNode }> = ({ docRef, className, content }) => {
    if (docRef.kind === 'task') {
        return (
            <span
                className={cn(className, 'underline decoration-dotted')}
                title="Opens this task's instructions in the app"
            >
                {content}
            </span>
        );
    }
    const section = docRef.kind === 'lesson' ? 'lessons' : 'considerations';
    return (
        <Link
            to={`/content/${section}/${encodeURIComponent(docRef.id)}`}
            className={cn(className, 'text-blue-600 underline')}
        >
            {content}
        </Link>
    );
};

/** A span's color: a `*_background` highlight tints behind the text, any other color tints the text. */
const inlineColorClass = (color: BlockColor) =>
    color.endsWith('_background') ? getBackgroundClass(color) : getBlockColors(color).text;

const backgroundBox = (color: BlockColor) => {
    const bg = getBackgroundClass(color);
    return bg ? cn('rounded-md px-2 py-1', bg) : undefined;
};

const numberToAlpha = (value: number) => {
    let num = Math.max(1, Math.floor(value));
    let output = '';

    while (num > 0) {
        const remainder = (num - 1) % 26;
        output = String.fromCharCode(97 + remainder) + output;
        num = Math.floor((num - 1) / 26);
    }

    return output;
};

const numberToRoman = (value: number) => {
    let num = Math.max(1, Math.floor(value));
    let result = '';

    for (const [arabic, roman] of RomanNumeralMap) {
        while (num >= arabic) {
            result += roman;
            num -= arabic;
        }
    }

    return result;
};

const RomanNumeralMap: Array<[number, string]> = [
    [1000, 'm'],
    [900, 'cm'],
    [500, 'd'],
    [400, 'cd'],
    [100, 'c'],
    [90, 'xc'],
    [50, 'l'],
    [40, 'xl'],
    [10, 'x'],
    [9, 'ix'],
    [5, 'v'],
    [4, 'iv'],
    [1, 'i'],
];
